import { useState, useEffect } from 'react';
import { getEmotionLabels, submitFeedback, submitGuestFeedback } from '../api/client';
import { getOrCreateGuestIdentifier } from '../utils/guestIdentifier';

interface InlineFeedbackFormProps {
  text: string;
  detectedEmotions: string[];
  predictedEmotions: string[];
  predictedSarcasm: boolean;
  languageCode: string;
  selectedEmotions?: string[];
  comment?: string;
  onEmotionsChange?: (emotions: string[]) => void;
  onCommentChange?: (comment: string) => void;
  sarcasmFeedback?: boolean | null;
  onSarcasmFeedbackChange?: (value: boolean | null) => void;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function InlineFeedbackForm({
  text,
  detectedEmotions,
  predictedEmotions,
  predictedSarcasm,
  languageCode,
  selectedEmotions: propSelectedEmotions,
  comment: propComment,
  onEmotionsChange,
  onCommentChange,
  sarcasmFeedback: propSarcasmFeedback,
  onSarcasmFeedbackChange,
  onSuccess,
  onCancel
}: InlineFeedbackFormProps) {
  const [availableEmotions, setAvailableEmotions] = useState<string[]>([]);
  const [selectedEmotions, setSelectedEmotions] = useState<string[]>(propSelectedEmotions || []);
  const [comment, setComment] = useState(propComment || '');
  const [sarcasmFeedback, setSarcasmFeedback] = useState<boolean | null>(
    propSarcasmFeedback ?? null
  );

  // Update local state when props change
  useEffect(() => {
    setSelectedEmotions(propSelectedEmotions || []);
  }, [propSelectedEmotions]);

  useEffect(() => {
    setComment(propComment || '');
  }, [propComment]);

  useEffect(() => {
    setSarcasmFeedback(propSarcasmFeedback ?? null);
  }, [propSarcasmFeedback]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Load emotion labels when component mounts or language changes
  useEffect(() => {
    const loadEmotionLabels = async () => {
      setLoading(true);
      setError('');
      
      try {
        const response = await getEmotionLabels(languageCode);
        const allEmotions = response.data.emotions;
        
        // Filter out already detected emotions
        const filteredEmotions = allEmotions.filter(
          (emotion: string) => !detectedEmotions.includes(emotion)
        );
        
        setAvailableEmotions(filteredEmotions);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Failed to load emotion labels:', err);
        }
        setError('Failed to load emotion options. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadEmotionLabels();
  }, [languageCode, detectedEmotions]);

  // Toggle emotion selection
  const toggleEmotion = (emotionLabel: string) => {
    let newSelectedEmotions: string[];
    if (selectedEmotions.includes(emotionLabel)) {
      newSelectedEmotions = selectedEmotions.filter(e => e !== emotionLabel);
    } else {
      if (selectedEmotions.length < 5) { // Limit to 5 emotions
        newSelectedEmotions = [...selectedEmotions, emotionLabel];
      } else {
        return; // Don't change if limit reached
      }
    }
    setSelectedEmotions(newSelectedEmotions);
    onEmotionsChange?.(newSelectedEmotions);
  };

  // Submit feedback
  const handleSubmit = async () => {
    if (selectedEmotions.length === 0 && !comment.trim() && sarcasmFeedback === null) {
      setError('Please select at least one emotion, set sarcasm feedback, or add a comment.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const feedbackData = {
        text,
        predicted_emotions: predictedEmotions,
        predicted_sarcasm: predictedSarcasm,
        sarcasm_feedback: sarcasmFeedback,
        suggested_emotions: selectedEmotions,
        comment: comment.trim(),
        language_code: languageCode,
      };

      if (token) {
        // Authenticated user
        await submitFeedback(feedbackData);
      } else {
        // Guest user
        const guestIdentifier = getOrCreateGuestIdentifier();
        await submitGuestFeedback(feedbackData, guestIdentifier);
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 1500);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Your session has expired. Please login again.');
      } else if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait before submitting again.');
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="p-6 bg-green-500/10 border border-green-500/30 rounded-xl">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-3 text-green-300">
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Thank you for your feedback!</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-slate-700 mt-6 pt-6">
      <div className="bg-slate-900/80 rounded-xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center">
          <svg className="h-5 w-5 mr-2 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          Suggest Additional Emotions
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
            <span className="ml-2 text-slate-400">Loading emotions...</span>
          </div>
        ) : (
          <>
            {/* Emotion Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Select emotions you think are missing ({selectedEmotions.length}/5):
              </label>
              
              {availableEmotions.length === 0 ? (
                <p className="text-sm text-slate-400 italic p-4 bg-slate-800 rounded-lg">
                  No additional emotions available - all emotions for this language have been detected!
                </p>
              ) : (
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-slate-800 rounded-lg border border-slate-700">
                  {availableEmotions.map((emotionLabel) => (
                    <button
                      key={emotionLabel}
                      type="button"
                      onClick={() => toggleEmotion(emotionLabel)}
                      disabled={!selectedEmotions.includes(emotionLabel) && selectedEmotions.length >= 5}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                        selectedEmotions.includes(emotionLabel)
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {emotionLabel}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sarcasm Feedback */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Sarcasm detected by AI: <span className="font-semibold">{predictedSarcasm ? 'Yes' : 'No'}</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSarcasmFeedback(true);
                    onSarcasmFeedbackChange?.(true);
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                    sarcasmFeedback === true
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  👍 Correct
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSarcasmFeedback(false);
                    onSarcasmFeedbackChange?.(false);
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                    sarcasmFeedback === false
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  👎 Incorrect
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSarcasmFeedback(null);
                    onSarcasmFeedbackChange?.(null);
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                    sarcasmFeedback === null
                      ? 'bg-slate-600 text-slate-200'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  🤷 Not sure
                </button>
              </div>
            </div>

            {/* Comment Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Additional comments (optional):
              </label>
              <textarea
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  onCommentChange?.(e.target.value);
                }}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 border border-slate-700 bg-slate-800 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-slate-100 placeholder-slate-500"
                placeholder="Any additional feedback about the emotion detection..."
              />
              <div className="text-xs text-slate-500 mt-1">
                {comment.length}/500 characters
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  (selectedEmotions.length === 0 &&
                    !comment.trim() &&
                    sarcasmFeedback === null)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 
